import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCredit } from "../../context/CreditContext";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { LogOut, Wallet2, Plus, User, ShoppingCart, Package, Swords, Crown, Users, Menu } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "../ui/dropdown-menu";
import AuthModal from "../AuthModal";
import WalletModal from "../WalletModal";
import InventoryModal from "../InventoryModal";
import ProfileModal from "../ProfileModal";

const NAV = [
  { id: "cases", label: "Cases", Icon: Package },
  { id: "battles", label: "Battles", Icon: Swords },
  { id: "vip", label: "VIP Members", Icon: Crown },
  { id: "affiliates", label: "Affiliates", Icon: Users },
];

export default function TopBar({ tab, setTab, onVip, onAffiliates, onToggleChat, onOpenMenu }) {
  const { user, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signin");
  const [profileOpen, setProfileOpen] = useState(false);

  const openAuth = (mode) => { setAuthMode(mode); setAuthOpen(true); };

  React.useEffect(() => {
    const a = () => openAuth("signin");
    window.addEventListener("uc:open-auth", a);
    return () => window.removeEventListener("uc:open-auth", a);
  }, []);

  const onNav = (n) => {
    if (n.label === "VIP Members") return onVip();
    if (n.label === "Affiliates") return onAffiliates();
    setTab(n.id);
  };

  return (
    <header className="sticky top-0 z-30 border-b border-[var(--uc-border)] bg-[var(--uc-header)] backdrop-blur-xl">
      <div className="flex h-16 items-center justify-between gap-3 px-4 md:h-[68px] md:px-6">
        <div className="flex items-center gap-3 md:gap-6">
          <button onClick={onOpenMenu} data-testid="mobile-menu-btn" aria-label="Menu"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--uc-border)] bg-white/5 text-white hover:bg-white/10 lg:hidden">
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/" className="group flex items-center gap-2" data-testid="site-logo">
            <img src="/uc-box-purple.png" alt="unboxcases" className="h-9 w-auto select-none object-contain transition-transform group-hover:scale-105" draggable={false} />
            <span className="hidden font-display text-[20px] font-extrabold lowercase tracking-[-0.02em] text-white sm:inline">
              unbox<span className="bg-gradient-to-r from-[#A78BFA] to-[#7C3AED] bg-clip-text text-transparent">cases</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" data-testid="top-nav-bar">
            {NAV.map((n) => {
              const isActive = (n.label === "Cases" && tab === "cases") ||
                (n.label === "Battles" && tab === "battles");
              return (
                <button
                  key={n.key || n.label}
                  data-testid={`nav-${(n.key || n.label).toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => onNav(n)}
                  className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold transition-colors
                    ${isActive ? "bg-white/10 text-white" : "text-[var(--uc-muted)] hover:text-white hover:bg-white/5"}`}
                >
                  <n.Icon className="h-4 w-4" /> {n.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {user ? <CreditWidget /> : (
            <div className="flex items-center gap-2">
              <button onClick={() => openAuth("signin")} data-testid="signin-btn"
                className="rounded-xl border border-[var(--uc-border)] bg-white/5 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/10 sm:px-4">
                Sign in
              </button>
              <button onClick={() => openAuth("signup")} data-testid="signup-btn"
                className="rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] px-3 py-2 text-sm font-bold text-white shadow-lg shadow-purple-900/30 transition-all hover:from-[#7C3AED] hover:to-[#5B21B6] sm:px-4">
                Sign up
              </button>
            </div>
          )}

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="user-menu-trigger"
                  className="gap-2 rounded-xl border-[var(--uc-border)] bg-white/5 font-semibold text-white hover:bg-white/10">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--uc-primary)] text-white"><User className="h-3.5 w-3.5" /></span>
                  <span className="hidden max-w-[110px] truncate sm:inline">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-xl border-[var(--uc-border)] bg-[#1B1730] text-white">
                <DropdownMenuLabel className="truncate text-xs font-normal text-[var(--uc-muted)]">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => setProfileOpen(true)} data-testid="profile-menu-item"
                  className="cursor-pointer gap-2 rounded-lg text-white focus:bg-white/10">
                  <User className="h-4 w-4" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="logout-btn"
                  className="cursor-pointer gap-2 rounded-lg text-[#F472B6] focus:bg-white/10 focus:text-[#F472B6]">
                  <LogOut className="h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />
      <ProfileModal open={profileOpen} onOpenChange={setProfileOpen} />
    </header>
  );
}

function CreditWidget() {
  const { credit } = useCredit();
  const [walletOpen, setWalletOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [depositPromo, setDepositPromo] = useState(null);

  React.useEffect(() => {
    const h = (e) => { setDepositPromo(e?.detail?.promo || null); setWalletOpen(true); };
    window.addEventListener("uc:open-deposit", h);
    return () => window.removeEventListener("uc:open-deposit", h);
  }, []);

  return (
    <>
      <div className="flex items-center gap-1.5 rounded-xl border border-[var(--uc-border)] bg-white/5 px-2.5 py-1.5 sm:px-3">
        <Wallet2 className="h-4 w-4 text-[var(--uc-green)]" />
        <span data-testid="credit-balance" className="font-grotesk text-sm font-semibold text-white">${credit.toFixed(2)}</span>
      </div>
      <button data-testid="inventory-open-btn" onClick={() => setInvOpen(true)} aria-label="Inventory"
        className="flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--uc-border)] bg-white/5 text-white transition-colors hover:bg-white/10">
        <ShoppingCart className="h-4 w-4" />
      </button>
      <button data-testid="wallet-open-btn" onClick={() => setWalletOpen(true)} aria-label="Deposit"
        className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white shadow-lg shadow-purple-900/30 transition-all hover:from-[#7C3AED] hover:to-[#5B21B6]">
        <Plus className="h-4 w-4" />
      </button>
      <WalletModal open={walletOpen} onOpenChange={(o) => { setWalletOpen(o); if (!o) setDepositPromo(null); }} initialTab="deposit" initialPromo={depositPromo} />
      <InventoryModal open={invOpen} onOpenChange={setInvOpen} />
    </>
  );
}
